import React, { useCallback } from "react";
import { css, useTheme } from "@emotion/react";
import { isArray } from "lodash";

import { FieldList, OptionalOnFieldChangeArgs } from "core/hooks/useForm";
import FormLabelledField from "core/Components/Form/FormLabelledField";
import FormDropDown from "core/Components/Form/FormDropDown";
import { FormDropdownOption } from "core/Components/Form/DropDown";
import SimpleCheckboxGroup from "core/Components/Form/SimpleCheckboxGroup";
import RadioGroup from "core/Components/Form/RadioGroup";
import Radio from "core/Components/Form/Radio";
import FormInput from "core/Components/Form/FormInput";
import SimpleCheckbox from "core/Components/Form/SimpleCheckbox";
import { getFontStylesForCss } from "theme/fontSizes";

type Props = {
    formFields: Array<FormField|Array<FormField>>,
    fields: any,
    errors: any,
    onFieldChange: (name: string, value: any, opArgs?: OptionalOnFieldChangeArgs) => void,
    mandatoryCheckKey?: string,
    registrantIndex?: number,
    className?: string,
};

const RegistrationForm: React.FC<Props> = ({
    formFields,
    fields,
    errors,
    onFieldChange,
    mandatoryCheckKey = 'isMandatoryForMainRegistrant',
    registrantIndex = null,
    className = '',
}) => {
    const theme = useTheme();

    const initialFields: any = { additionalRegistrants: [], registrantIndex };

    const getIsTextInput = (type: FormFieldVariant) => (
        ['email', 'text', 'number'].includes(type)
    );

    const getIsHalfLength = (fieldArray: Array<FormField>, index: number) => {
        return (fieldArray.length % 2 === 0) || (index !== (fieldArray.length - 1))
    };

    const getInitialValue = (field: FieldList) => {
        switch(true) {
            case field.type === 'checkbox':
                return false;
            case field.type === 'checkbox-multiple':
                return field.options.map((op: EventRegistrationOption) => ({
                    id: op.id,
                    name: op.id,
                    label: op.label,
                    code: op.code,
                    value: false,
                }));
            default:
                return '';
        }
    }

    formFields.flat().forEach((f: FormField) => initialFields[f.id] = getInitialValue(f));

    const onEmailChange = useCallback((name: string, value: any) => {
        const emailId = formFields.flat().find((ff: FormField) => ff.code === 'email')?.id;
        const emailForInvoiceId = formFields.flat().find((ff: FormField) => ff.code === 'email_to_invoice')?.id;
        const areEmailsEqual = emailId && emailForInvoiceId && (fields[emailId] === fields[emailForInvoiceId]);

        onFieldChange(
            name,
            value,
            {
                ...areEmailsEqual ? { extraFields: [{ name: emailForInvoiceId, value }] } : {}
            },
        );
    }, [formFields, onFieldChange, fields]);

    const formLabelledFieldStyles = ({
        isHalfWidth = false,
        isLast = false,
        isTextField = false,
        isCheckboxField = false,
    } : {
        isHalfWidth?: boolean,
        isLast?: boolean,
        isTextField?: boolean,
        isCheckboxField?: boolean,
    } = {}) => css`
        --margin-bottom: ${isLast ? '0' : '36px'};
        
        width: ${isHalfWidth ? '45%' : '100%'};
        margin-bottom: var(--margin-bottom);

        &:last-child {
            margin-bottom: var(--margin-bottom);
        }

        .label {
            ${getFontStylesForCss('18')};
        }

        .error {
            white-space: pre-wrap;
            line-height: 100%;
        }
        
        ${isCheckboxField && css`
            .error-label {
                padding-left: 30px;
                padding-top: 3px;
                
                ${theme.breakpoints.down("md")} {
                    padding-top: 2px;
                }
            }
        `};      

        ${theme.breakpoints.down('md')} {
            margin-bottom: ${isLast ? `0` : `${isTextField ? 29 : 34}px`}
        }
    `;

    const textInputContainerStyles = ({ isLast = false } = {}) => css`
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: space-between;

        ${theme.breakpoints.down('md')} {
            ${!isLast && css` margin-bottom: 14px; `};
        }
    `;

    const formInputStyle = css`
        ${theme.breakpoints.down('md')} {
            padding-top: 0;
        }

        > input {
            ${theme.breakpoints.down('md')} {
                height: 40px;
                box-shadow: inset 0 0 0 1px ${theme.colours.grey[100]},
                inset 0 0 0 100px ${theme.colours.grey[100]};
            }
        }
    `;

    const inputTextStyle = css`
        margin-right: 12px;
        font-size: 14px;
        line-height: 17px;
    `;

    const checkboxStyle = css`
        span {
            padding-top: 2px;

            ${inputTextStyle};
        }
    `;

    const checkboxGroupStyle = css`
        margin-bottom: 0 !important;
    `;

    return (
        <>
            {formFields.map((field: FormField | Array<FormField>, index: number) => (
                <React.Fragment key={index}>
                    {isArray(field) ? (
                        <div
                            css={textInputContainerStyles({ isLast: index === formFields.length - 1})}
                        >
                            {field.map((f: FormField, i: number) => (
                                <FormLabelledField
                                    name={f.id}
                                    error={errors?.[f.id]}
                                    label={f.label}
                                    errorAsterisk={!!f[mandatoryCheckKey as keyof typeof f]}
                                    key={f.id}
                                    css={formLabelledFieldStyles({
                                        isHalfWidth: getIsHalfLength(field, i),
                                        isTextField: getIsTextInput(f.type),
                                        isCheckboxField: f.type === "checkbox",
                                        isLast: index === formFields.length - 1 &&
                                            i >= (field.length - Math.abs((field.length % 2) - 2)),
                                    })}
                                    className={className}
                                >
                                    <FormInput
                                        hasError={!!errors?.[f.id]}
                                        label={f.label}
                                        name={f.id}
                                        onChange={f.code === 'email' ? onEmailChange : onFieldChange}
                                        css={formInputStyle}
                                        value={fields[f.id]}
                                        variant={f.type === 'email' ? 'text' : f.type}
                                    />
                                </FormLabelledField>
                            ))}
                        </div>
                    ) : (
                        <FormLabelledField
                            name={field.id}
                            error={errors?.[field.id]}
                            errorAsterisk={field.isMandatoryForMainRegistrant}
                            key={field.id}
                            css={formLabelledFieldStyles({
                                isLast: index === formFields.length - 1,
                                isCheckboxField: field.type === 'checkbox',
                            })}
                            className={className}
                            {...field.type !== 'checkbox' && { label: field.label }}
                        >
                            {field.type === 'dropdown' && field.options && (
                                <FormDropDown
                                    selected={fields[field.id]}
                                    setSelected={(selectedOption: FormDropdownOption) => {
                                        onFieldChange(field.id, selectedOption)
                                    }}
                                    options={field.options.map((op: EventRegistrationOption) => ({
                                        id: op.id,
                                        value: op.label,
                                        code: op.code,
                                    }))}
                                    hasError={!!errors?.[field.id]}
                                />
                            )}
                            {field.type === 'checkbox' && (
                                <SimpleCheckbox
                                    id={field.id}
                                    css={checkboxStyle}
                                    hasError={!!errors?.[field.id]}
                                    key={index}
                                    label={field.label}
                                    name={field.id}
                                    onChange={onFieldChange}
                                    value={fields[field.id]}
                                    isCircle
                                />
                            )}
                            {field.type === 'checkbox-multiple' && field.options && (
                                <SimpleCheckboxGroup
                                    id={field.id}
                                    groupName={field.id}
                                    fields={
                                        fields?.[field.id] ||
                                        field.options.map((op: any) => ({
                                            id: op.id,
                                            name: op.id,
                                            label: op.label,
                                            code: op.code,
                                            value: fields[field.id]?.find((f: any) => f.name === op.id)?.value || false,
                                        }))
                                    }
                                    hasError={!!errors?.[field.id]}
                                    onFieldChange={onFieldChange}
                                    checkboxStyle={checkboxGroupStyle}
                                    isTwoColumn
                                />
                            )}
                            {field.type === 'radio' && (
                                <RadioGroup>
                                    {field.options?.map(
                                        (op: EventRegistrationOption) => (
                                            <Radio
                                                label={op.label}
                                                name={op.id}
                                                onChange={() => {
                                                    onFieldChange(
                                                        field.id,
                                                        {
                                                            id: op.id,
                                                            value: op.label,
                                                            code: op.code,
                                                        }
                                                    )
                                                }}
                                                selected={fields[field.id]?.id === op.id}
                                                value={fields[field.id]?.id === op.id}
                                                key={op.id}
                                            />
                                        )
                                    )}
                                </RadioGroup>
                            )}
                            {getIsTextInput(field.type) && (
                                <FormInput
                                    hasError={!!errors[field.id]}
                                    label={field.label}
                                    name={field.id}
                                    onChange={field.code === 'email' ? onEmailChange : onFieldChange}
                                    value={fields[field.id]}
                                    variant={field.type === 'email' ? 'text' : field.type}
                                    css={formInputStyle}
                                />
                            )}
                        </FormLabelledField>
                    )}
                </React.Fragment>
            ))}
        </>
    )
}

export default RegistrationForm;
